import React from 'react';

// Every explanation on the floor plan in one place (Fernando, 2026-09-28: an ⓘ on each number,
// the buttons explained under the plan, and a guide for "when we actually need to use it in 5
// years and don't remember anything about this project"). Keep it true: when the page changes,
// this file changes with it. The technical record is docs/WAREHOUSE-MODEL.md. Claude, 2026-09-28.

/** What each number above the plan shows, how it is counted, and where it comes from. */
export const STAT_INFO: Record<string, string> = {
  Pallets:
    'Every euro pallet (1200 × 800) standing in a pallet row today, each with its own permanent ID (P-0001…). ' +
    '"3 per side" pallets stand with the long 120 cm side along the row and carry 3 stock sections on each face; ' +
    '"2 per side" stand turned, the 80 cm side along the row, 2 sections per face. "Empty" = no stock on any of ' +
    'its sections right now. Source: the pallet count per row (wms/warehouse/pallet-counts-2026-09-28.csv).',
  'Metal bays':
    'Every metal shelf bay (one bay = one stock section), each with its own ID (B-0001…). HI280 is the newer ' +
    'system (rows A–C, 129 cm bays), R3000 the older SSI Schäfer (rows D–I and Z, 130 cm). Measured per row in ' +
    'wms/warehouse/measurements-2026-09-25.csv.',
  Legs:
    'The frames (legs) the metal rows stand on. Neighbouring bays share a leg, so a run of n bays needs n + 1; a gap ' +
    'in a row (extinguisher, pipe) starts a new run and costs one extra leg. A floor space or reserved number is ' +
    'not a bay. Counted from the bays and gaps — nobody counted legs by hand.',
  Shelves:
    'Installed shelves in the metal bays, including the top restock shelf (the floor is the first box row and has ' +
    'no shelf). Only known for bays someone has walked with the units walk (Warehouse → Calibrate → a face letter); ' +
    'until then it shows —.',
  'Back beams / crosses':
    'HI280: every shelf needs a back beam to be sturdy — the count per bay is recorded in the units walk (it follows ' +
    'the shelf count unless changed). R3000: the shelf hangs between the legs and needs no beam; instead the walk ' +
    'records whether the bay has a cross brace. Shows — until bays are walked.',
  'Stock sections':
    'Stock sections on a pallet or bay (a section = one stack on a pallet face, or one metal bay; its code is what ' +
    'the products carry as stock location, e.g. M 23). "With stock" = at least one piece recorded there now. ' +
    '"Reserved" = numbers kept free in a row (the holes at J|K\'s pillars, A 5, Z 22 …): a place with no pallet — ' +
    'stock on one is a wrong location. "Floor" = a numbered floor space without a shelf (A 1).',
  'Pieces placed':
    'Pieces in stock (all three shops, from the product table) whose location is a section on a pallet, a bay or a ' +
    'floor space. The warning counts stock whose location has no place in the plan — a wrong location on the ' +
    'product, or something the plan is missing; the list is in the side panel ("Stock on no pallet or bay"). ' +
    'The special locations Ä and Ö are not in this count.',
  'Max fill':
    'How many more pallets fit if each pallet row grows toward the top wall as far as its green fill line. The ' +
    'default lines (2026-09-28): J|K as far as H|I reaches (using the measured 153 cm and 328 cm either side of its ' +
    'pillar), R|S as far as T|U, L|M three more. No row may grow into the mezzanine. Drag a line in "Plan moves" to ' +
    'try another limit; the pallets that fit are drawn light. "Sections" = the stock sections those pallets add.',
  'Planned moves':
    'Pallets someone dragged to a new place in "Plan moves". It is a plan only — the dashed outline shows where the ' +
    'pallet stands today. Nothing changes in the stock locations or the shops.',
};

/** The ⓘ itself. The explanation is rendered by the caller as a strip under the numbers —
 *  not as a popover: the numbers strip scrolls sideways, and an absolutely positioned
 *  popover inside a scrolling strip is clipped to the strip's height (found 2026-09-28). */
export function InfoButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} aria-label="What is this?"
      className={`ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] leading-none border ${
        open ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-600 text-gray-400 hover:text-white'}`}>i</button>
  );
}

export function InfoStrip({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <div className="px-3 py-2 border-b border-gray-800 bg-[#11141c] text-xs text-gray-200">
      <span className="text-gray-400 uppercase tracking-wider text-[11px] mr-2">{label}</span>
      {STAT_INFO[label]}
      <button onClick={onClose} className="ml-3 text-gray-500 hover:text-white">Close</button>
    </div>
  );
}

/** The buttons and marks, explained under the plan. */
export function ToolbarHelp() {
  const item = (b: string, t: string) => (
    <div><span className="text-gray-200 font-medium">{b}</span> — {t}</div>
  );
  return (
    <div className="space-y-0.5">
      {item('Find', 'type a stock code (M 23) or a unit ID (P-0012, B-0003): the piece is selected and shown in the side panel.')}
      {item('Whole hall', 'fits the warehouse to the page width and goes back to the top.')}
      {item('Colour', '"pallet direction" colours pallets by how they stand (3 or 2 sections per side); "box stacks" shows the box type of every stack (S, M, SL, LL, ML), grey = not marked yet. Mark them in the side panel: click a pallet, then "All S" or per section.')}
      {item('Measure', 'click a start and an end (a pallet, a bay, a station entrance, which it snaps to, or any spot): the green line is the walking route around everything in the way, with its length; "straight" is as the crow flies. Click Measure again to stop.')}
      {item('Plan moves', 'switches planning on: drag a pallet to where it should stand (or Turn 90° in the side panel), drag the green fill line of a row to change its maximum, place and move the stations and their entrances (side panel, Stations), draw a temporary obstacle (+ Obstacle). Everything is a plan: no stock location, shop or label changes.')}
      {item('+ Obstacle', '(in Plan moves) drag a rectangle over something blocking the way, like a stack of boxes or a cart; measurements walk around it. Click it to add a note or remove it.')}
      {item('+ Add a pallet', '(managers and admins) click the highlighted place at the top of a row: the side panel shows the codes the new pallet gets, its stacks to choose and what it adds to picking; Add pallet makes it real. Print its location labels from there.')}
      {item('Guide', 'how this page works, where its data comes from and how to keep it right.')}
      {item('Mouse', 'scroll = the page scrolls as usual · Ctrl + scroll = zoom around the cursor · drag the background = move the view · click a piece = details · Esc = cancel a tool.')}
    </div>
  );
}

/** The full guide — written for someone who opens this page years from now. */
export function FloorPlanGuide({ onClose }: { onClose: () => void }) {
  const H = ({ children }: { children: React.ReactNode }) => <h3 className="text-white font-semibold mt-5 mb-1">{children}</h3>;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-center overflow-auto p-6" onClick={onClose}>
      <div className="max-w-3xl w-full bg-[#1a1d27] border border-gray-700 rounded-xl p-6 text-sm text-gray-300 h-fit"
        onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-baseline">
          <h2 className="text-xl font-semibold text-white">Floor plan — guide</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">Close ✕</button>
        </div>
        <p className="mt-2">
          The warehouse at Mäeküla 9 (unit 2a + 2b) drawn to scale: every pallet and every metal shelf bay as its own
          piece, where it stands today, with its stock sections and the stock on them. Built in September 2026 from
          Fernando's measurements. The detailed technical record is <code>docs/WAREHOUSE-MODEL.md</code> in the WMS
          code; the raw measurements are in the <code>wms/warehouse/</code> folder.
        </p>

        <H>How to read the drawing</H>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Top = the top wall</b> (cargo doors, packing); bottom = the office wall. <b>Left = the left wall</b> (row Z),
            right = the right wall (row A). This is the packer's view: A is on the packer's left.</li>
          <li><b>Metal rows</b> A, B|C, D|E, F|G, H|I (right side of the hall) and Z (left wall): grey pieces, one per bay.
            B|C means two rows back to back — B faces the A-B aisle, C the C-D aisle.</li>
          <li><b>Pallet rows</b> J|K … X|Y (left side of the hall): green = pallet with 3 sections per side, brown = 2 per
            side. Both faces of a pallet carry sections: J 94 and K 6 are the two sides of the same pallet.</li>
          <li><b>Dashed boxes</b>: reserved numbers (holes the numbering keeps, e.g. at J|K's pillars) and floor spaces
            (A 1). Red dashes = stock is recorded on it.</li>
          <li><b>Light, see-through pallets</b> with a green outline: a place where a pallet can still go (maximum fill).
            The <b>green line</b> across a row is its fill line.</li>
          <li><b>Hatched area</b> top left: the mezzanine (vahepõrand) — P&P stations, return and inbound stations, a
            small office under it. Its width is approximate.</li>
          <li>Faded pieces have no stock on them right now.</li>
          <li><b>Blue boxes</b>: the stations (P&P 1 and 2, the return station, inbound 1 and 2); the orange dot is the
            entrance a walk starts from. <b>Red boxes</b>: temporary obstacles.</li>
          <li>With <b>Colour: box stacks</b> every pallet shows its stacks, each face in its own half: S, M, SL, LL, ML.
            A pallet with two kinds (e.g. S on one face, M on the other) shows both, and its setup reads "S+M".</li>
        </ul>

        <H>The three kinds of ID</H>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Stock section</b> (SEC-00001 …): a <i>place</i> in this warehouse. Its <b>code</b> (M 23) is the label
            the products carry as stock location. A code can be renumbered; the section ID never changes.</li>
          <li><b>Pallet P-0001 …</b> and <b>bay B-0001 …</b>: the <i>furniture</i>. It keeps its ID when it moves — also to
            another warehouse. Pallets were numbered row by row from the left wall, each row from the bottom wall up.</li>
          <li><b>Reserved number</b>: a section with no pallet (a pillar hole). If stock shows up on one, the product has a
            wrong location. <b>Floor space</b>: a numbered spot without a shelf where something can stand (A 1).</li>
          <li><b>Ä</b> is the shelf Z 29 (returns that are not stored back); <b>Ö</b> is the P&P table (socks).</li>
        </ul>

        <H>What you can do here</H>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Look something up</b>: click a piece (or type in Find) — the side panel lists its codes on both faces and
            the pieces on each.</li>
          <li><b>Find stock that has no place</b>: side panel, "Stock on no pallet or bay". Each line says why — a code the
            plan does not have, a reserved hole, or not a location code at all. Fix the product's location, or tell the
            plan what is missing.</li>
          <li><b>Plan a move</b>: "Plan moves", then drag a pallet (or "Turn 90°"). The dashed outline stays where it
            stands today; "Put it back" undoes one, "Drop all planned moves" all of them. A plan changes nothing else.</li>
          <li><b>Try a capacity</b>: in "Plan moves" drag a row's green fill line; the row's "+N" and "Max fill" follow.
            "back to default" in the side panel returns a line to the agreed limit.</li>
          <li><b>Mark the box stacks</b>: click a pallet, then "All S" (or M, SL, LL, ML), or one section at a time for a
            combo; "Next pallet up the row" walks the row. Tap a marked type again to clear it.</li>
          <li><b>Measure a walk</b>: "Measure", then two clicks. The route goes around every pallet and bay (where they stand),
            every obstacle, the stations and the mezzanine (switchable in the side panel), down the middle of the aisles, on a
            20 cm grid — measured by the server, the same number the P&P statistics and the aisle pages use.</li>
          <li><b>Stations</b>: in "Plan moves", side panel, Stations: Place (click where it stands), Entrance (click the
            opening), drag to move. Default size 250 × 200 cm.</li>
          <li><b>Obstacles</b>: in "Plan moves", "+ Obstacle", then drag a rectangle; a removed obstacle stays in the
            database as inactive (it happened).</li>
          <li>Who: everyone can look; warehouse staff, managers and admins can plan (viewers cannot).</li>
        </ul>

        <H>Where the numbers come from</H>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>measurements-2026-09-25.csv</b>: aisle widths at both walls, the first and last code of every face with
            their distances from the walls, the gaps (pillars, extinguishers, pipes), the mezzanine note.</li>
          <li><b>pallet-counts-2026-09-28.csv</b>: per pallet row the sections per side and the number of pallets (counted
            from the bottom wall); per metal row the number of bays.</li>
          <li>Stock: the WMS product table (all three shops), live.</li>
          <li>Shelves, level heights, back beams, cross braces, pallet box types: the <b>units walk</b> (Warehouse →
            Calibrate → a face letter). Until a face is walked those are unknown.</li>
          <li>Known approximations: the hall depth (46.8 m) is derived from the lease plan's area, not measured; pallet
            positions are counted from the bottom wall; the mezzanine width is estimated; J|K comes out ~70 cm longer
            than its top-end chain — most likely the width of the pillar there.</li>
        </ul>

        <H>When the warehouse changes</H>
        <ul className="list-disc pl-5 space-y-1">
          <li>A pallet moved for real, or the rows rebuilt: plan it here first. Turning a plan into new codes and writing
            them to the shops is the <b>move planner</b> (the next step — not built as of September 2026).</li>
          <li>A pallet added (2026-10-02): <b>+ Add a pallet</b> (managers and admins). It joins its row at the top end,
            on the highlighted "can go here" place; its stock sections continue the row's numbering on both faces (e.g.
            L 85–87 / M 15–13), a pillar on the way takes two numbers per face as a reserved hole, and the stacks start
            like the pallet below. The row's two aisles get the new picking stops when that only adds stops — an aisle
            whose rebuild would also change existing stops (Q-R today) is named, rebuild it on its aisle page. When a
            face's numbers run out (K: K 1 is the last) the WMS stops and says so — that naming is a person's decision.
            Nothing goes to the shops: the new places are empty until goods are moved there. Removing a pallet, a new
            row: not built yet.</li>
          <li>Jeans pallets, the gluing station and the stations under the mezzanine are not in the plan yet.</li>
          <li>A new warehouse: the same model works — new sections (places), the same pallet IDs (furniture).</li>
        </ul>
        <p className="mt-5 text-xs text-gray-500">Page built by Claude for Fernando, 2026-09-28.</p>
      </div>
    </div>
  );
}
